import { useEffect, useState } from "react";
import { api, API, fmtDate, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export function Finance() {
  const [invoices, setInvoices] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  useEffect(() => { api.get(`/admin/invoices?month=${month}`).then(r => setInvoices(r.data)).catch(() => setInvoices([])); }, [month]);
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Finance</h1>
      <div className="mt-4 flex gap-2 items-end">
        <div><label className="text-xs">Month</label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="finance-month" /></div>
        <a href={`${API}/admin/exports/monthly?month=${month}`} target="_blank" rel="noreferrer">
          <Button className="btn-brand" data-testid="finance-download-zip">Download month ZIP</Button>
        </a>
      </div>
      <div className="mt-6 cgc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr>{["Number","Kind","Buyer","GSTIN","Total","Issued"].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {invoices.map(i => (
              <tr key={i.id} className="border-t border-border">
                <td className="p-3 font-mono">{i.number}</td>
                <td className="p-3">{i.kind}</td>
                <td className="p-3">{i.buyer?.name}</td>
                <td className="p-3 font-mono text-xs">{i.buyer?.gstin || "—"}</td>
                <td className="p-3 font-mono">{fmtINR(i.grand_total_paise)}</td>
                <td className="p-3 text-xs">{fmtDate(i.issued_at)}</td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={6} className="text-center p-10 text-stone-500">No invoices this month</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-stone-500 mt-4">Tax setup requires review by CGC&apos;s accountant before live launch. Current invoices are recorded without GST breakup.</p>
    </div>
  );
}

export function Coupons() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/coupons").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const [c, setC] = useState({ code:"", discount_type:"percent", value:"", max_uses:"100" });
  const create = async () => {
    try {
      if (!c.code) return toast.error("Coupon code is required");
      const val = parseFloat(c.value);
      if (isNaN(val) || val <= 0) return toast.error("Enter a valid discount value");
      const payload = {
        code: c.code.toUpperCase(),
        max_uses: parseInt(c.max_uses || "100"),
      };
      if (c.discount_type === "percent") {
        payload.percent_off = val;
      } else {
        payload.flat_off_paise = Math.round(val * 100);
      }
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created"); load(); setC({ code:"", discount_type:"percent", value:"", max_uses:"100" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Coupons</h1>
      <div className="mt-6 cgc-card p-5 flex flex-col sm:flex-row gap-3 items-center">
        <Input placeholder="CODE" value={c.code} onChange={(e) => setC({...c,code:e.target.value})} data-testid="new-coupon-code" />
        <select
          className="border border-border rounded-md px-3 py-2 text-sm bg-background h-10 shrink-0"
          value={c.discount_type}
          onChange={(e) => setC({...c, discount_type: e.target.value})}
        >
          <option value="percent">% Discount</option>
          <option value="flat">Flat ₹ (Rupees) Off</option>
        </select>
        <Input
          placeholder={c.discount_type === "percent" ? "% off (e.g. 10)" : "₹ off (e.g. 100)"}
          type="number"
          step="any"
          value={c.value}
          onChange={(e) => setC({...c,value:e.target.value})}
          data-testid="new-coupon-pct"
        />
        <Input placeholder="Max uses" type="number" value={c.max_uses} onChange={(e) => setC({...c,max_uses:e.target.value})} data-testid="new-coupon-max" />
        <Button onClick={create} className="btn-brand shrink-0" data-testid="new-coupon-submit">Add</Button>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.id} className="cgc-card p-4">
            <div className="font-mono text-lg">{c.code}</div>
            <div className="text-sm text-stone-500">{c.percent_off ? `${c.percent_off}% off` : `₹${((c.flat_off_paise||0)/100).toFixed(0)} off`}</div>
            <div className="text-xs mt-1">Used {c.used}/{c.max_uses}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  const save = async () => {
    try { await api.put("/settings", s); toast.success("Saved"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const testEmail = async () => {
    try { const r = await api.post("/admin/emails/test", { to: s.support_email }); toast.info(r.data.status === "sent" ? "Sent" : `SMTP: ${r.data.status}${r.data.error ? ` (${r.data.error})` : ""}`); }
    catch (e) { toast.error("Failed"); }
  };
  if (!s) return <div>Loading…</div>;
  const upd = (k, v) => setS({ ...s, [k]: v });
  const fld = (k, label, type="text") => (<div key={k}><label className="text-xs">{label}</label><Input type={type} value={s[k] || ""} onChange={(e) => upd(k, type==="number"?parseFloat(e.target.value):e.target.value)} data-testid={`set-${k}`} /></div>);
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Settings</h1>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {fld("brand_name","Brand name")}{fld("brand_tagline","Tagline")}
        {fld("main_site_url","Main site URL")}{fld("support_email","Support email")}
        {fld("legal_name","Legal name")}{fld("gstin","GSTIN")}
        {fld("legal_address","Legal address")}{fld("invoice_prefix","Invoice prefix")}
        {fld("checkout_charge_percent","Checkout charge (%)","number")}
        <div className="flex items-center gap-2 pt-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={s.checkout_charge_enabled !== false}
              onChange={(e) => upd("checkout_charge_enabled", e.target.checked)}
              data-testid="set-checkout_charge_enabled"
              className="rounded border-gray-300 text-[color:var(--brand-terracotta)]"
            />
            <span className="font-medium">Enable platform checkout charge</span>
          </label>
        </div>
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <h2 className="font-display text-xl font-bold">Homepage Impact Ticker</h2>
        <p className="text-xs text-stone-500 mt-1">Customize the statistics ticker shown on the public homepage.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium">Stat 1: Funds Raised Override (₹ in Rupees)</label>
            <Input
              type="number"
              step="any"
              placeholder="Leave blank to use actual booking revenue"
              value={s.impact_raised_paise !== undefined && s.impact_raised_paise !== null ? (s.impact_raised_paise / 100) : ""}
              onChange={(e) => upd("impact_raised_paise", e.target.value === "" ? null : Math.round(parseFloat(e.target.value) * 100))}
              data-testid="set-impact_raised_paise"
              className="mt-1"
            />
            <span className="text-[11px] text-stone-400">Leave blank for live revenue, or enter custom ₹ amount (e.g. 50000).</span>
          </div>
          {fld("impact_raised_label", "Stat 1: Funds Raised Label (e.g. Raised on this platform)")}
          <div className="p-3 rounded-lg border border-border bg-stone-50 dark:bg-stone-900/40">
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Stat 2: Programs on Calendar</label>
            <div className="text-sm font-medium mt-1 text-emerald-600 flex items-center gap-1.5">
              <span>● Automatic</span>
              <span className="text-xs text-stone-500 font-normal">(Computed live from active programs)</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Stat 3: Supporters Registered (Count)</label>
            <Input
              type="number"
              placeholder="Leave blank for automatic calculation"
              value={s.impact_supporters_count !== undefined && s.impact_supporters_count !== null ? s.impact_supporters_count : ""}
              onChange={(e) => upd("impact_supporters_count", e.target.value === "" ? null : parseInt(e.target.value))}
              data-testid="set-impact_supporters_count"
              className="mt-1"
            />
            <span className="text-[11px] text-stone-400">Leave blank to use live booking count, or enter a custom number.</span>
          </div>
          {fld("impact_supporters_label", "Stat 3: Supporters Label (e.g. Supporters registered)")}
          <div>
            <label className="text-xs font-medium">Stat 4: Chapters (Count)</label>
            <Input
              type="number"
              placeholder="e.g. 1"
              value={s.impact_chapters_count !== undefined && s.impact_chapters_count !== null ? s.impact_chapters_count : 1}
              onChange={(e) => upd("impact_chapters_count", e.target.value === "" ? 1 : parseInt(e.target.value))}
              data-testid="set-impact_chapters_count"
              className="mt-1"
            />
          </div>
          {fld("impact_chapters_label", "Stat 4: Chapters Label (e.g. Current chapters)")}
          <div className="md:col-span-2">
            {fld("impact_tagline", "Ticker Tagline Note (e.g. Live numbers from actual bookings — never inflated)")}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-6">
        <Button onClick={save} className="btn-brand" data-testid="settings-save">Save Settings</Button>
        <Button onClick={testEmail} variant="outline" data-testid="settings-test-email">Send test email</Button>
      </div>
    </div>
  );
}
